import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bot, History } from 'lucide-react';
import { getGitRepoAttachment } from '@reflex/shared';
import { timeAgo } from '@reflex/ui/lib/format';
import { normalizeRepoSlug } from '@reflex/ui/lib/repo-slug';
import {
  CommandDialog,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@reflex/ui/components/ui/command';
import { KbdCombo } from '@reflex/ui/components/shared/Kbd';
import { PaletteSearchResult } from '@reflex/ui/components/layout/PaletteSearchResult';
import { useSessionJumpEntries } from '@/hooks/agents/useSessionJumpEntries';
import { useHotkey } from '@/hooks/ui/useHotkey';
import { hotkeyKeys, type HotkeyId } from '@/lib/hotkeys';
import { useOrgRoutes } from '@/lib/useOrgRoutes';
import type { SessionJumpMode } from '@/lib/session-jump';
import type { Agent } from '@/lib/types';

const kbdHintClassName =
  'border-border/80 bg-gradient-to-b from-muted/60 to-muted font-semibold text-foreground shadow-sm';

const MODE_COPY: Record<SessionJumpMode, { placeholder: string; heading: string; empty: string }> =
  {
    all: {
      placeholder: 'Jump to session...',
      heading: 'Sessions',
      empty: 'No sessions match.',
    },
    recent: {
      placeholder: 'Recent sessions...',
      heading: 'Recent',
      empty: 'No recent sessions.',
    },
  };

/** The shortcut that opens each mode, matching the hint footer. */
const MODE_HOTKEY: Record<SessionJumpMode, HotkeyId> = {
  all: 'jumpToSession',
  recent: 'recentSessions',
};

export interface SessionJumpPaletteProps {
  /** The open mode, or `null` when the palette is closed. */
  mode: SessionJumpMode | null;
  onModeChange: (mode: SessionJumpMode | null) => void;
}

/** The one line of context under a run's title: its repo, else its agent type. */
function sessionHint(agent: Agent): string | undefined {
  const repo = getGitRepoAttachment(agent)?.repoSlug;
  if (repo) return normalizeRepoSlug(repo);
  return agent.agentType ?? undefined;
}

/**
 * Jump-to-session palette: `⌥⌘O` searches every session the viewer can see,
 * `⌥⌘E` lists the ones they most recently opened. Modelled on IntelliJ's
 * Go-to-Class / Recent-Files pair — two keystrokes that put you in a session
 * without touching the sidebar. (The chords carry ⌥ because the browser takes
 * plain ⌘O and ⌘E for its own menus before the page ever sees them; the
 * `HOTKEYS` table in `@/lib/hotkeys` is where that is recorded.)
 *
 * Kept separate from the global `⌘K` palette on purpose. That surface is a
 * command index where sessions would be one group competing with navigation,
 * settings, and plugin providers; this one is a single-purpose list where the
 * first row is always the session you meant, so the muscle memory is
 * keystroke-type-Enter.
 *
 * The bindings live here, next to the surface they open, matching the `⌘K`
 * binding in `CommandPalette` — do not add a second listener for these keys.
 */
export function SessionJumpPalette({ mode, onModeChange }: SessionJumpPaletteProps) {
  const [query, setQuery] = useState('');

  const openMode = useCallback(
    (next: SessionJumpMode) => {
      // Switching modes with the palette already open starts a fresh query:
      // the two lists answer different questions, so carrying the old text
      // over would filter a list the user has not seen yet.
      setQuery('');
      onModeChange(next);
    },
    [onModeChange],
  );

  useHotkey(MODE_HOTKEY.all, () => openMode('all'));
  useHotkey(MODE_HOTKEY.recent, () => openMode('recent'));

  // The dialog only ever reports itself closing (Escape, overlay click, a
  // selected row); opening is driven by the shortcut and the ⌘K action rows.
  const close = useCallback(() => {
    setQuery('');
    onModeChange(null);
  }, [onModeChange]);

  const copy = MODE_COPY[mode ?? 'all'];

  return (
    <CommandDialog
      open={mode !== null}
      onOpenChange={close}
      showCloseButton={false}
      // Rows arrive pre-ranked by `rankSessionJumpEntries`; cmdk's own filter
      // would re-order them and drop the field-level (prompt / id / repo) hits
      // it cannot see.
      shouldFilter={false}
    >
      <CommandInput placeholder={copy.placeholder} value={query} onValueChange={setQuery} />
      <CommandList className="max-h-[400px]">
        {/* Mounted only while open, so the always-mounted palette does not hold
            an agents query open on every page in the app. */}
        {mode !== null ? <SessionJumpResults mode={mode} query={query} onSelected={close} /> : null}
      </CommandList>
      <div
        data-testid="session-jump-kbd-hints"
        className="hidden items-center justify-end gap-4 border-t border-border bg-muted/30 px-3 py-2 md:flex"
      >
        <div className="flex items-center gap-1.5 text-2xs text-muted-foreground">
          <KbdCombo keys={hotkeyKeys(MODE_HOTKEY.all)} size="sm" keyClassName={kbdHintClassName} />
          <span>all sessions</span>
        </div>
        <div className="flex items-center gap-1.5 text-2xs text-muted-foreground">
          <KbdCombo
            keys={hotkeyKeys(MODE_HOTKEY.recent)}
            size="sm"
            keyClassName={kbdHintClassName}
          />
          <span>recent</span>
        </div>
        <div className="flex items-center gap-1.5 text-2xs text-muted-foreground">
          <KbdCombo keys={['enter']} size="sm" keyClassName={kbdHintClassName} />
          <span>open</span>
        </div>
      </div>
    </CommandDialog>
  );
}

interface SessionJumpResultsProps {
  mode: SessionJumpMode;
  query: string;
  onSelected: () => void;
}

/**
 * The rows themselves. Split from the dialog so the queries behind them mount
 * with the open palette and unmount with it.
 */
function SessionJumpResults({ mode, query, onSelected }: SessionJumpResultsProps) {
  const navigate = useNavigate();
  const routes = useOrgRoutes();
  const { entries, isLoading } = useSessionJumpEntries(query, mode);
  const copy = MODE_COPY[mode];

  const open = (agentId: string) => {
    onSelected();
    navigate(routes.agent(agentId));
  };

  if (entries.length === 0) {
    // Not `CommandEmpty`: that renders off cmdk's own filtered count, which
    // stays at zero while `shouldFilter` is off, so it would show under a full
    // list of rows.
    return (
      <CommandItem disabled value="session-jump-status">
        {isLoading ? 'Loading sessions...' : copy.empty}
      </CommandItem>
    );
  }

  return (
    <CommandGroup heading={copy.heading}>
      {entries.map(({ agent, recencyAt, visited }) => (
        <PaletteSearchResult
          key={agent.id}
          value={agent.id}
          label={agent.name}
          hint={sessionHint(agent)}
          icon={visited ? History : Bot}
          trailing={
            <span className="text-2xs text-muted-foreground tabular-nums">
              {timeAgo(recencyAt, { compact: true })}
            </span>
          }
          onSelect={() => open(agent.id)}
        />
      ))}
    </CommandGroup>
  );
}
